import * as React from 'react';
import { Button, Icon, Typography } from '@/components/common';
import { LabelFileUploaded } from '@/components/common/upload';
import { ButtonsForm } from '@/components/dashboard';
import Icons from '@/const/icons';
import useModalPreviewPage from '@/hooks/useModalPreviewPage';
import { DemandComplaintPreview } from '../../email-preview';
import { UploadPDFModal } from '@/components/modals/UploadPDFModal';
import { useNotification } from '@/hooks';
import {
	auditService,
	GET_CASE_DOCUMENTS,
	GET_CASE_STEPS_STATUS,
	GET_COMPLAINT_DOCUMENTS,
} from '@/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { complaintService } from '@/api/complaint';
import { useRouter } from 'next/router';
import { PDFComplaint, PDFDemandLetter } from '@/components/screens/dashboard';
import { pdf } from '@react-pdf/renderer';
import {
	ApprovePhase,
	CaseDetailsLawyer,
	DocumentActivePhase,
	DocumentType,
	StepStatusCase,
} from '@/interfaces';
import { caseApplicantService } from '@/api/caseApplicant';
import { downloadPDFByUrl, getLatestDocumentByType } from '@/lib';
import { CaseContext } from '@/context/CaseContext';
import clsx from 'clsx';
import { downloadBlobPDF } from '@/lib/dowloadBlobPdf';
import { getValidCaseId } from '@/utils';

interface DemandComplaintI {
	phaseId: number;
	caseDetails: CaseDetailsLawyer | undefined;
	step: StepStatusCase;
	type: 'demand letter' | 'complaint';
	caseDocuments: DocumentActivePhase[] | undefined;
	switched: boolean;
}

export const DemandComplaint: React.FC<DemandComplaintI> = ({
	phaseId,
	step,
	caseDetails,
	type,
	caseDocuments,
	// switched,
}) => {
	const router = useRouter();
	const caseId = getValidCaseId(router.query.id);
	const id = caseId;
	const { toastError, toastSuccess } = useNotification();
	const { isCaseReadOnly } = React.useContext(CaseContext);
	const queryClient = useQueryClient();
	const [pdfFile, setPdfFile] = React.useState<File | undefined>();
	const [pdfUrl, setPdfUrl] = React.useState<DocumentActivePhase>();
	const [fileError, setFileError] = React.useState<boolean>(false);
	const [isUploadDocumentModalOpen, setIsUploadDocumentModalOpen] =
		React.useState<boolean>(false);
	const { ModalPreviewPage, hidePreviewPage, showPreviewPage } =
		useModalPreviewPage();

	const isStepApproved = step.status === 'approved';
	const documentType: DocumentType =
		type === 'complaint' ? 'Complaint' : 'Demand Letter';

	const stepOrder = ['Investigation', 'Applicant Intake'];

	const hasUnsentDocument = React.useMemo(() => {
		if (pdfFile) {
			return true;
		}

		if (!caseDocuments) {
			return false;
		}

		const relevantDocuments = caseDocuments
			.filter(
				(document) =>
					document.document_type ===
					(type === 'demand letter' ? 'Demand Letter' : 'Complaint')
			)
			.sort(
				(a, b) =>
					new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
			);

		if (relevantDocuments.length === 0) {
			return false;
		}

		const mostRecentDocument = relevantDocuments[0];
		return (
			mostRecentDocument.sent_at === null || mostRecentDocument.sent_at === ''
		);
	}, [caseDocuments, type, pdfFile]);

	const { data: documentInfo } = useQuery({
		queryKey: [GET_COMPLAINT_DOCUMENTS, id],
		queryFn: () =>
			complaintService.getComplaintDocuments(
				id as string,
				type === 'complaint' ? 'CLASS_ACTION' : 'MASS_ACTION'
			),
		enabled: !!id,
	});

	React.useEffect(() => {
		if (caseDocuments && caseDetails) {
			const urlFile = getLatestDocumentByType(
				caseDocuments,
				step.name === 'Demand Letter' ? 'MASS_ACTION' : 'CLASS_ACTION'
			);
			setPdfUrl(urlFile);
		}
	}, [caseDocuments, caseDetails]);

	const downloadPDF = async (): Promise<void> => {
		if (caseDetails?.phase_name && stepOrder.includes(caseDetails?.phase_name))
			return;
		try {
			const blob = await pdf(
				type === 'demand letter' ? (
					<PDFDemandLetter
						tableData={documentInfo?.data[0]}
						caseName={caseDetails?.title}
					/>
				) : (
					<PDFComplaint
						documentInfo={documentInfo?.data[0]}
						caseName={caseDetails?.title}
					/>
				)
			).toBlob();

			const link = document.createElement('a');
			link.href = URL.createObjectURL(blob);
			link.download = 'Client Names.pdf';

			document.body.appendChild(link);
			link.click();

			document.body.removeChild(link);
		} catch (error) {
			toastError('Error downloading PDF');
		}
	};

	const handleComplete = async (file: File | undefined): Promise<void> => {
		if (file) {
			setPdfFile(file);
			try {
				await caseApplicantService.postUploadDemandComplaint(
					id as string,
					documentType,
					file
				);
				await caseApplicantService.postDemandComplaint(
					id as string,
					documentType
				);
				queryClient.refetchQueries({ queryKey: [GET_CASE_DOCUMENTS] });
				if (caseId) {
					queryClient.refetchQueries({
						queryKey: [GET_CASE_STEPS_STATUS, caseId],
					});
				}
			} catch (error) {
				toastError(
					'An error occurred while confirming completion. Please try again.'
				);
			} finally {
				setFileError(false);
			}
		} else {
			setPdfFile(undefined);
		}
	};

	const handleDeleteDocument = async (): Promise<void> => {
		try {
			await caseApplicantService.deleteActivaCaseDocument(
				id as string,
				documentType
			);
			queryClient.refetchQueries({ queryKey: [GET_CASE_DOCUMENTS] });
			if (caseId) {
				queryClient.refetchQueries({
					queryKey: [GET_CASE_STEPS_STATUS, caseId],
				});
			}
		} catch (error) {
			toastError(
				'An error occurred while confirming completion. Please try again.'
			);
		} finally {
			setFileError(false);
			setPdfFile(undefined);

			setPdfUrl(undefined);
			setIsUploadDocumentModalOpen(false);
		}
	};

	const handlePreview = (): void => {
		showPreviewPage();
	};

	const handleApprove = async (): Promise<void> => {
		try {
			const data: ApprovePhase = {
				case_id: caseDetails?.id || '',
				lawyer_stepper_id: phaseId,
				stepper_form_id: step.step_id,
			};
			await auditService.postApprovePhase(data);
			toastSuccess('The case section has been successfully approved.');
			if (caseId) {
				queryClient.refetchQueries({
					queryKey: [GET_CASE_STEPS_STATUS, caseId],
				});
			}
			setPdfFile(undefined);
			hidePreviewPage();
		} catch (error) {
			toastError('There was an error approving the case section.');
		}
	};

	const handleDownload = async (): Promise<void> => {
		if (pdfFile) {
			await downloadBlobPDF(pdfFile);
			return;
		}
		if (pdfUrl) {
			await downloadPDFByUrl(pdfUrl.file_path, pdfUrl.document_name);
		}
	};

	return (
		<div>
			<Typography type="semibold-16" className="text-black">
				Download Client Names
			</Typography>
			<Typography type="regular-12" className="text-opacity-black mt-3 mb-6">
				Click to download the complete list of clients associated with this
				case. The file will include key client details needed for preparing the{' '}
				{type}.
			</Typography>
			<LabelFileUploaded
				label="Client Names.pdf"
				icon={
					caseDetails?.phase_name && stepOrder.includes(caseDetails?.phase_name)
						? Icons.documentDownloadGray
						: Icons.documentDownload
				}
				iconClassName={clsx('text-primary', {
					'cursor-not-allowed':
						caseDetails?.phase_name &&
						stepOrder.includes(caseDetails?.phase_name),
				})}
				onClick={downloadPDF}
				className={clsx({
					'cursor-not-allowed':
						caseDetails?.phase_name &&
						stepOrder.includes(caseDetails?.phase_name),
				})}
			/>
			<Typography type="semibold-16" className="text-black mt-12 capitalize">
				Upload {type}
			</Typography>
			<Typography type="regular-12" className="text-opacity-black mt-3 mb-6">
				Click to open the upload window. Please ensure you have reviewed the
				client list before submitting the {type}.
			</Typography>
			{pdfUrl || pdfFile ? (
				<div className="flex gap-2">
					<LabelFileUploaded
						icon={Icons.documentDownload}
						label={pdfUrl ? `${pdfUrl.document_name}.pdf` : pdfFile?.name || ''}
						onClick={handleDownload}
					/>
					{hasUnsentDocument && (
						<button className="-mt-2.5">
							<Icon
								src={Icons.trash}
								className="!w-4 !h-4 cursor-pointer text-main-red"
								onClick={() => {
									if (!isCaseReadOnly) {
										handleDeleteDocument();
									}
								}}
							/>
						</button>
					)}
				</div>
			) : (
				<>
					<Button
						label={`Upload ${type}`}
						icon={Icons.documentUpload}
						iconLeft
						size="medium"
						className="!py-1 !px-[35px]  !border-green-base !h-[30px] mb-2 capitalize"
						classNameButton="!w-4 !h-4"
						disabled={
							caseDetails?.phase_name &&
							stepOrder.includes(caseDetails?.phase_name)
						}
						onClick={() => setIsUploadDocumentModalOpen(true)}
					/>
					{fileError && (
						<div className="flex gap-2">
							<Icon
								src={Icons.exclamationCircle}
								width={16}
								height={16}
								className="!text-main-red"
							/>
							<Typography type="regular-12" className="text-main-red">
								Please upload the required file.
							</Typography>
						</div>
					)}
				</>
			)}
			<div className="mt-8">
				<ButtonsForm
					onClickPreview={() => {
						if (
							!isStepApproved &&
							caseDetails?.phase_name &&
							stepOrder.includes(caseDetails?.phase_name)
						)
							return;

						if (!pdfFile && !pdfUrl) {
							setFileError(true);
						} else {
							handlePreview();
						}
					}}
					isCompleteDisabled={
						!isStepApproved &&
						caseDetails?.phase_name &&
						stepOrder.includes(caseDetails?.phase_name)
					}
					completeButtonClassName={
						!pdfFile && !pdfUrl ? 'opacity-60 cursor-default' : ''
					}
					onlyOneButton
					isApproved={isStepApproved}
				/>
			</div>
			<UploadPDFModal
				isOpen={isUploadDocumentModalOpen}
				setIsModalOpen={setIsUploadDocumentModalOpen}
				setPdfFile={handleComplete}
				pdfFile={pdfFile}
				customFileType={documentType}
				title={
					type === 'complaint' ? 'Upload Complaint' : 'Upload Demand Letter'
				}
				customSuccessToast={
					type === 'complaint'
						? 'The complaint has been successfully uploaded.'
						: 'The demand letter has been successfully uploaded.'
				}
			/>
			<ModalPreviewPage
				handleClickApprove={handleApprove}
				handleClickBack={hidePreviewPage}
				handleClickEdit={hidePreviewPage}
				backCustomLabel="Back to edit"
				title={
					isStepApproved || isCaseReadOnly
						? 'This is your preview page.'
						: `This is your preview page. Go back to make changes or 'Approve' to stage it for going live.`
				}
				hideApproveButton={isStepApproved || isCaseReadOnly}
				hideEditButton={true}
			>
				<DemandComplaintPreview
					caseName={caseDetails?.title || ''}
					caseType={caseDetails?.case_type || undefined}
					type={type}
					caseDocuments={caseDocuments}
				/>
			</ModalPreviewPage>
		</div>
	);
};
